import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  LinearProgress,
  Chip,
  Grid
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { 
  Star, 
  TrendingUp, 
  TrendingDown, 
  RateReview,
  ThumbUp
} from '@mui/icons-material';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const WidgetContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: '24px'
  }
}));

const RatingCard = styled(Card)(({ theme }) => ({
  width: '100%',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #3b7ab8 100%)',
  border: 'none',
  boxShadow: '0 4px 16px rgba(30, 58, 95, 0.2)',
  overflow: 'hidden',
  position: 'relative',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${fadeIn} 0.5s ease-out`,
  WebkitTapHighlightColor: 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-60px',
    right: '-40px',
    width: '120px',
    height: '120px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    borderRadius: '50%'
  },
  [theme.breakpoints.up('md')]: {
    flex: '0 0 280px',
    width: 'auto',
    borderRadius: '16px',
    padding: '28px',
    boxShadow: '0 8px 32px rgba(30, 58, 95, 0.25)',
    '&::before': {
      width: '150px',
      height: '150px'
    }
  }
}));

const RatingCircle = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(10px)',
  border: '3px solid rgba(255, 255, 255, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '12px',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.up('md')]: {
    width: '100px',
    height: '100px',
    marginBottom: '16px'
  }
}));

const RatingNumber = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '28px',
  fontWeight: 700,
  color: 'white',
  lineHeight: 1,
  [theme.breakpoints.up('md')]: {
    fontSize: '36px'
  }
}));

const RatingLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.8)',
  marginBottom: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  [theme.breakpoints.up('md')]: {
    fontSize: '13px',
    marginBottom: '12px'
  }
}));

const StarsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '3px',
  marginBottom: '10px',
  [theme.breakpoints.up('md')]: {
    gap: '4px',
    marginBottom: '12px'
  }
}));

const ReviewCount = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  color: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  [theme.breakpoints.up('md')]: {
    fontSize: '13px',
    gap: '6px'
  }
}));

const MetricsContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '12px',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px'
  }
}));

const MetricCard = styled(Card)(({ theme, accentcolor }) => ({
  borderRadius: '12px',
  padding: '16px',
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.25s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.5s ease-out`,
  WebkitTapHighlightColor: 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: accentcolor || '#3b82f6'
  },
  '&:active': {
    transform: 'scale(0.98)'
  },
  [theme.breakpoints.up('sm')]: {
    borderRadius: '14px',
    padding: '20px'
  },
  [theme.breakpoints.up('md')]: {
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
      borderColor: '#d1d5db'
    },
    '&:active': {
      transform: 'translateY(-2px)'
    }
  }
}));

const MetricHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
  [theme.breakpoints.up('md')]: {
    marginBottom: '16px'
  }
}));

const MetricIconBox = styled(Box)(({ theme, bgcolor }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  backgroundColor: bgcolor || '#eff6ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '18px'
  },
  [theme.breakpoints.up('md')]: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    '& svg': {
      fontSize: '20px'
    }
  }
}));

const TrendBadge = styled(Box)(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  padding: '3px 6px',
  borderRadius: '5px',
  fontSize: '10px',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  backgroundColor: trend === 'up' ? '#ecfdf5' : '#fef2f2',
  color: trend === 'up' ? '#059669' : '#dc2626',
  '& svg': {
    fontSize: '12px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    '& svg': {
      fontSize: '14px'
    }
  }
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '22px',
  fontWeight: 700,
  color: '#111827',
  lineHeight: 1,
  marginBottom: '4px',
  [theme.breakpoints.up('md')]: {
    fontSize: '28px'
  }
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#6b7280',
  marginBottom: '10px',
  [theme.breakpoints.up('md')]: {
    fontSize: '13px',
    marginBottom: '12px'
  }
}));

const ProgressBar = styled(Box)(({ theme, value, color }) => ({
  height: '5px',
  borderRadius: '2.5px',
  backgroundColor: '#f1f5f9',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    display: 'block',
    height: '100%',
    width: `${value}%`,
    background: color || 'linear-gradient(90deg, #3b82f6, #6366f1)',
    borderRadius: '2.5px',
    transition: 'width 1s ease-out'
  },
  [theme.breakpoints.up('md')]: {
    height: '6px',
    borderRadius: '3px',
    '&::after': {
      borderRadius: '3px'
    }
  }
}));

const CompareText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  color: '#9ca3af',
  marginTop: '6px',
  [theme.breakpoints.up('md')]: {
    fontSize: '11px',
    marginTop: '8px'
  }
}));

const InteractivePerformanceWidget = ({ reputationData }) => {
  const [animationComplete, setAnimationComplete] = useState(false);

  const data = reputationData || {
    yourBusiness: { rating: 4.6, responseRate: 52, negativeRatio: 6, reviewCount: 20 },
    cityAverage: { rating: 4.2, responseRate: 36, negativeRatio: 23, reviewCount: 180 }
  };

  const business = data.yourBusiness;
  const cityAvg = data.cityAverage;
  const rating = business.rating || 4.0;
  const responseRate = business.responseRate || 45;
  const negativeRatio = business.negativeRatio || 8;
  const reviewCount = business.reviewCount || 0;

  useEffect(() => {
    const timer = setTimeout(() => setAnimationComplete(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const metrics = [
    {
      id: 'response',
      icon: <RateReview sx={{ color: '#3b82f6' }} />,
      iconBg: '#eff6ff',
      accentColor: 'linear-gradient(90deg, #3b82f6, #6366f1)',
      value: `${Math.round(responseRate)}%`,
      label: 'Response Rate',
      progress: responseRate,
      trend: responseRate > 50 ? 'up' : 'down',
      trendValue: responseRate > cityAvg?.responseRate ? `+${Math.round(responseRate - cityAvg?.responseRate)}%` : `-${Math.round(cityAvg?.responseRate - responseRate)}%`,
      compare: `City avg: ${cityAvg?.responseRate || 36}%`
    },
    {
      id: 'positive',
      icon: <ThumbUp sx={{ color: '#10b981' }} />,
      iconBg: '#ecfdf5',
      accentColor: 'linear-gradient(90deg, #10b981, #14b8a6)',
      value: `${Math.round(100 - negativeRatio)}%`,
      label: 'Positive Reviews',
      progress: 100 - negativeRatio,
      trend: negativeRatio < 15 ? 'up' : 'down',
      trendValue: negativeRatio < 15 ? '+8%' : '-5%',
      compare: `${negativeRatio}% negative`
    },
    {
      id: 'total',
      icon: <Star sx={{ color: '#8b5cf6' }} />,
      iconBg: '#f3e8ff',
      accentColor: 'linear-gradient(90deg, #8b5cf6, #a855f7)',
      value: reviewCount.toString(),
      label: 'Total Reviews',
      progress: Math.min(100, (reviewCount / 100) * 100),
      trend: 'up',
      trendValue: '+3',
      compare: 'All time'
    }
  ];

  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      const isFilled = i < fullStars || (i === fullStars && hasHalfStar);
      stars.push(
        <Star
          key={i}
          sx={{ 
            fontSize: '18px', 
            color: isFilled ? '#fbbf24' : 'rgba(255,255,255,0.3)'
          }}
        />
      );
    }
    return stars;
  };

  return (
    <WidgetContainer>
      <RatingCard>
        <RatingLabel>Overall Rating</RatingLabel>
        <RatingCircle>
          <RatingNumber>{rating.toFixed(1)}</RatingNumber>
        </RatingCircle>
        <StarsContainer>
          {renderStars()}
        </StarsContainer>
        <ReviewCount>
          📝 {reviewCount} reviews
        </ReviewCount>
      </RatingCard>

      <MetricsContainer>
        {metrics.map((metric) => (
          <MetricCard key={metric.id} accentcolor={metric.accentColor}>
            <MetricHeader>
              <MetricIconBox bgcolor={metric.iconBg}>
                {metric.icon}
              </MetricIconBox>
              <TrendBadge trend={metric.trend}>
                {metric.trend === 'up' ? <TrendingUp /> : <TrendingDown />}
                {metric.trendValue}
              </TrendBadge>
            </MetricHeader>
            
            <MetricValue>{metric.value}</MetricValue>
            <MetricLabel>{metric.label}</MetricLabel>
            
            <ProgressBar 
              value={animationComplete ? metric.progress : 0}
              color={metric.accentColor}
            />
            <CompareText>{metric.compare}</CompareText>
          </MetricCard>
        ))}
      </MetricsContainer>
    </WidgetContainer>
  );
};

export default InteractivePerformanceWidget;
